import { useSyncExternalStore } from 'react';

/** Console preferences, stored in this browser only (the theme is kept by next-themes). */
export interface Preferences {
  /** How the chat composer sends: Enter, or Cmd/Ctrl+Enter (Enter then adds a line). */
  sendWith: 'enter' | 'mod-enter';
  /** Dates and times across the console. */
  timeZone: 'local' | 'utc';
  /** Start every page with the navigation collapsed to icons. */
  compactSidebar: boolean;
}

const KEY = 'console.preferences';
export const DEFAULT_PREFERENCES: Preferences = {
  sendWith: 'enter',
  timeZone: 'local',
  compactSidebar: false,
};

const listeners = new Set<() => void>();
let cached: Preferences | null = null;

function read(): Preferences {
  if (cached) return cached;
  try {
    const raw = localStorage.getItem(KEY);
    cached = { ...DEFAULT_PREFERENCES, ...(raw ? (JSON.parse(raw) as Partial<Preferences>) : {}) };
  } catch {
    cached = DEFAULT_PREFERENCES;
  }
  return cached;
}

export function getPreferences(): Preferences {
  return read();
}

export function setPreference<K extends keyof Preferences>(key: K, value: Preferences[K]): void {
  cached = { ...read(), [key]: value };
  try {
    localStorage.setItem(KEY, JSON.stringify(cached));
  } catch {
    // Storage can be unavailable (private mode); the choice still applies for this visit.
  }
  for (const l of listeners) l();
}

export function usePreferences(): Preferences {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      const onStorage = (e: StorageEvent) => {
        if (e.key !== KEY) return;
        cached = null;
        onChange();
      };
      window.addEventListener('storage', onStorage);
      return () => {
        listeners.delete(onChange);
        window.removeEventListener('storage', onStorage);
      };
    },
    read,
    () => DEFAULT_PREFERENCES,
  );
}
